import React, { FC, useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import { Swiper as SwiperCore } from 'swiper';
import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lightgallery-bundle.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-video.css';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import s from './BlockSlider.module.scss';
import { BACKEND_URL } from '../../../api';
import { ReactComponent as PlayButton } from './../../../assets/icons/play-button.svg';

interface BlockImage {
    id: number;
    type?: 'image' | 'video';
    src: string;
    poster?: string;
}

interface BlockSliderProps {
    images: BlockImage[];
}

const BlockSlider: FC<BlockSliderProps> = ({ images }) => {
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const pausedVideosRef = useRef<Set<HTMLVideoElement>>(new Set());

    useEffect(() => {
        if (!images || images.length === 0) {
            return;
        }
        
        const videoElements = document.querySelectorAll(`.${s.mainSlide} video`) as NodeListOf<HTMLVideoElement>;
        
        // Проверяем, что lightgallery не открыт
        const isLightGalleryOpen = document.querySelector('.lg-on') !== null;
        if (isLightGalleryOpen) {
            videoElements.forEach((video) => {
                if (video && document.body.contains(video)) {
                    try {
                        video.pause();
                        video.currentTime = 0;
                    } catch (err) {
                    }
                }
            });
            return;
        }
        
        const pauseHandlers: Map<HTMLVideoElement, () => void> = new Map();
        
        videoElements.forEach((video) => {
            if (!document.body.contains(video)) {
                return;
            }
            
            const handlePause = () => {
                if (video.closest('.swiper-slide-active') && document.body.contains(video)) {
                    pausedVideosRef.current.add(video);
                }
            };
            
            video.addEventListener('pause', handlePause);
            pauseHandlers.set(video, handlePause);
        });
        
        videoElements.forEach((video) => {
            if (!document.body.contains(video)) {
                return;
            }
            
            const isActive = video.closest('.swiper-slide-active');
            
            if (isActive) {
                if (!pausedVideosRef.current.has(video)) {
                    video.muted = true; 
                    const playPromise = video.play();
                    
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                if (!document.body.contains(video)) {
                                    try {
                                        video.pause();
                                    } catch (e) {
                                    }
                                }
                            })
                            .catch((err) => {
                                if (err.name !== 'NotAllowedError' && 
                                    err.name !== 'AbortError' &&
                                    document.body.contains(video)) {
                                    console.error('Error playing video:', err);
                                }
                            });
                    }
                }
            } else {
                try {
                    // Проверяем наличие элемента перед паузой
                    if (document.body.contains(video)) {
                        video.pause();
                        video.currentTime = 0;
                    }
                } catch (err) {
                }
                pausedVideosRef.current.delete(video);
            }
        });
        
        return () => {
            pauseHandlers.forEach((handler, video) => {
                if (document.body.contains(video)) {
                    video.removeEventListener('pause', handler);
                }
            });
        };
    }, [activeIndex]);

    const handleGalleryOpen = () => {
        document.body.style.overflow = 'hidden';
        const videoElements = document.querySelectorAll(`.${s.mainSlide} video`) as NodeListOf<HTMLVideoElement>;
        videoElements.forEach((video) => {
            if (video && document.body.contains(video)) {
                try {
                    video.pause();
                    video.currentTime = 0;
                } catch (err) {
                }
            }
        });
    };

    const handleGalleryClose = () => {
        document.body.style.overflow = 'auto';
        pausedVideosRef.current.clear();
    };

    if (!images || images.length === 0) {
        return null;
    }

    const customStyle: React.CSSProperties & { [key: string]: string } = {
        '--swiper-navigation-color': '#fff',
        '--swiper-pagination-color': '#fff',
    };

    return (
        <div className={s.wrapper}>
            <LightGallery
                selector=".block-gallery-item"
                elementClassNames={s.custom_wrapper_class}
                speed={0}
                plugins={[lgThumbnail, lgVideo]}
                download={false}
                counter={false}
                onAfterOpen={handleGalleryOpen}
                onAfterClose={handleGalleryClose}
            >
                <Swiper
                    slidesPerView={1}
                    spaceBetween={0}
                    style={customStyle}
                    navigation={images.length > 1}
                    thumbs={{ swiper: thumbsSwiper }}
                    modules={[FreeMode, Navigation, Thumbs]}
                    className={s.mainSwiper}
                    onSlideChange={(swiper: SwiperCore) => setActiveIndex(swiper.realIndex)}
                >
                    {images.map((image, index) => {
                        const mediaUrl = BACKEND_URL + image.src;
                        const posterUrl = image.poster ? BACKEND_URL + image.poster : undefined;
                        const itemType = image.type || 'image';
                        
                        let videoData: string | undefined;
                        if (itemType === 'video') {
                            const videoDataObj: {
                                source: Array<{ src: string; type: string }>;
                                attributes: {
                                    preload: boolean;
                                    playsinline: boolean;
                                    controls: boolean;
                                    poster?: string;
                                };
                            } = {
                                source: [{ src: mediaUrl, type: "video/mp4" }],
                                attributes: {
                                    preload: false,
                                    playsinline: true,
                                    controls: true
                                }
                            };
                            
                            if (posterUrl) {
                                videoDataObj.attributes.poster = posterUrl;
                            }
                            
                            videoData = JSON.stringify(videoDataObj);
                        }

                        return (
                            <SwiperSlide className={`${s.mainSlide} lg-item`} key={image.id || index}>
                                {itemType === 'image' ? (
                                    <a
                                        className='block-gallery-item'
                                        href={mediaUrl}
                                        data-src={mediaUrl}
                                    >
                                        <img src={mediaUrl} alt={`Block image ${index + 1}`} />
                                    </a>
                                ) : (
                                    <a
                                        className='block-gallery-item'
                                        data-video={videoData}
                                        data-poster={posterUrl}
                                        data-lg-size="1280-720"
                                    >
                                        <video
                                            muted
                                            controls
                                            poster={posterUrl}
                                            autoPlay
                                        >
                                            <source src={mediaUrl} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                        {posterUrl && <img src={posterUrl} alt={image.id.toString()} style={{ display: 'none' }} />}
                                    </a>
                                )}
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </LightGallery>
            {images.length > 1 && (
                <Swiper
                    onSwiper={setThumbsSwiper}
                    direction="horizontal"
                    spaceBetween={15}
                    watchSlidesProgress={true}
                    modules={[FreeMode, Navigation, Thumbs]}
                    className={s.navigationSwiper}
                    grabCursor
                    breakpoints={{
                        300: {
                            direction: 'horizontal',
                            slidesPerView: 3.4,
                            slidesPerGroup: 3.4,
                        },
                        992: {
                            direction: 'horizontal',
                            slidesPerView: 4.5,
                            slidesPerGroup: 4.5,
                        },
                    }}
                >
                    {images.map((image, index) => {
                        const mediaUrl = BACKEND_URL + image.src;
                        const posterUrl = image.poster ? BACKEND_URL + image.poster : undefined;
                        const itemType = image.type || 'image';
                        
                        return (
                            <SwiperSlide className={s.slide} key={image.id || index}>
                                {itemType === 'image' ? (
                                    <img src={mediaUrl} alt={`Thumbnail ${index + 1}`} />
                                ) : (
                                    <>
                                        {posterUrl ? (
                                            <img src={posterUrl} alt={image.id.toString()} />
                                        ) : (
                                            <video controls={false} muted>
                                                <source src={mediaUrl} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        )}
                                        <PlayButton className={s.play_button} />
                                    </>
                                )}
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            )}
        </div>
    );
};

export default BlockSlider;

